import {AzureLayer, isHeatMapDisabledXorIsOverlayPinEnabled} from "@esWebAPI/es/map/layer/azure/AzureLayer";
import { ILayerView, LayerInfo } from "@esWebAPI/es/model/Layer";
import {BubbleLayerOptions, layer, Map, source} from "azure-maps-control";
import {DatasourceManager} from "@esWebAPI/es/map/module/DatasourceManager";
import {ColorThematicStyler} from "@esWebAPI/es/map/styler/ColorThematicStyler";


export class AzurePieClusterBubbleLayer implements AzureLayer {

    private readonly _map: Map;
    private readonly datasourceManager: DatasourceManager;
    private readonly colorStyler: ColorThematicStyler;

    private _layerView: ILayerView;
    private _layerInfo: LayerInfo;

    private datasourceId: string|undefined;
    private _layer: layer.BubbleLayer|undefined;

    constructor(map: Map, layerView: ILayerView, layerInfo: LayerInfo,
                datasourceManager: DatasourceManager, colorStyler: ColorThematicStyler) {
        this._map = map;
        this._layerView = layerView;
        this._layerInfo = layerInfo;
        this.datasourceManager = datasourceManager;
        this.colorStyler = colorStyler;
    }

    private getOpacity(){
        return this.colorStyler.getOpacity(this._layerView);
    }

    private getOptions() {

        const serverColor = this._layerView.styleSettings.defaultStyle.color;
        const luminance = es.util.color.getLuminanceOfServerColor(serverColor);
        const transparency = es.util.color.getTransparency(serverColor);

        // if point transparency has been explicitly set then use that. if not, pick a slight transparency as a default
        const clusterTransparency = transparency > 0 ? transparency :
            Math.min(100, Math.max(0, parseInt(es.util.localStorage.get("es.clusterCircleTransparency") ?? 25))); // ensure val is between 0-100 (25 is good)

        const clusterColorWithTransparency = es.util.color.withTransparency(serverColor, clusterTransparency);
        const clusterRgbaColor = es.util.color.serverColorToRGB(clusterColorWithTransparency);

        // if symbol colour is dark then font and outline is dark, and vice-versa
        // rgba(46,60,140,1) = 4281220236 = #2E3C8C // new label dark navy
        // rgba(48,48,48,1) nearly black
        const fontAndOutlineColor = luminance < 0.5 ? "rgba(255,255,255,1)" : "rgba(48,48,48,1)";

        return {
            // scale the size of the clustered bubble based on the number of points/digits in the cluster
            //radius: 12,
            radius: [
                "step",
                ["get", "point_count"],
                10,      // default to 10px radius
                10, 12,  // point_count >= 10, radius is 12px
                100, 14  // point_count >= 100, radius is 14px
            ],
            filter: ['has', 'point_count'],
            color: clusterRgbaColor,
            strokeWidth: 1,
            strokeColor: fontAndOutlineColor,
            visible: this._layerView.enabled && isHeatMapDisabledXorIsOverlayPinEnabled(this._layerView),
            opacity: ['ceil', this.getOpacity()],
            strokeOpacity: ['ceil', this.getOpacity()],
            sourceLayer: this.datasourceId && this.datasourceManager.isPbf(this.datasourceId) ? 'es' : undefined,
        } as BubbleLayerOptions;
    }

    public async create(datasource: source.Source): Promise<void> {
        this.datasourceId = datasource.getId();
        this._layer = new layer.BubbleLayer(datasource.getId(), this.getLayerId(), this.getOptions());
        this._map.layers.add(this._layer);
        return Promise.resolve();
    }

    public async recreate(layerView: ILayerView, layerInfo: LayerInfo, datasource: source.Source) {
        this._layerView = layerView;
        this._layerInfo = layerInfo;
        return this.create(datasource);
    }

    public async update(layerView: ILayerView, layerInfo: LayerInfo): Promise<void> {
        this._layerView = layerView;
        this._layerInfo = layerInfo;
        let options = this.getOptions();
        this._layer?.setOptions(options);
        return Promise.resolve();
    }

    public remove(): void {
        const layerId = this.getLayerId();
        if (this._map.layers.getLayerById(layerId)) {
            this._map.layers.remove(layerId);
        }
        this._layer = undefined;
    }

    public hide(): void {
        this._layer?.setOptions({visible: false});
    }

    public show(): void {
        this._layer?.setOptions({visible: true});
    }

    public getLayerId(): string {
        return "pieClusterBubble_" + this._layerView.id;
    }

    public hideFeature(featureId: number): void {
        es.log.warn("Not implemented");
    }

    public showFeatures(): void {
        es.log.warn("Not implemented");
    }

    public getAzureLayer(): layer.BubbleLayer | undefined {
        return this._layer;
    }
}